/**
 * What somebody on a business card actually does: "Plumber", "Electrician",
 * "Realtor".
 *
 * Narrower than the partner category on purpose. The category (ten values,
 * fixed by the database) decides which monthly texts a partner gets; the line
 * of work is what the business card file is grouped by. Jonathon, 2026-10-04:
 * "i want the classification to be more specific like electrician or plumber
 * instead of trade etc" (ADR 0262).
 *
 * Free text, so a line of work nobody thought of can still be typed in, with a
 * list of common ones offered first. Typing one of those, in any spelling the
 * list knows, saves it the list's way, so "plumbing" and "Plumber" end up in
 * one group rather than two.
 */

import { partnerCategoryLabel, type PartnerCategory } from "./partners.js";

export interface LineOfWork {
  /** How it is shown and stored. */
  label: string;
  /** The partner category it belongs to, for the texting side. */
  category: PartnerCategory;
  /** Other ways a card or a person says the same thing. Lower case. */
  aliases: readonly string[];
}

/** The lines of work offered first, in the order the picker shows them. */
export const LINES_OF_WORK: readonly LineOfWork[] = [
  {
    label: "Realtor",
    category: "realtor",
    aliases: ["real estate", "real estate agent", "realty", "broker"],
  },
  {
    label: "Insurance agent",
    category: "insurance_agent",
    aliases: ["insurance", "insurance agency"],
  },
  {
    label: "Adjuster",
    category: "adjuster",
    aliases: ["claims adjuster", "independent adjuster", "public adjuster"],
  },
  {
    label: "Property manager",
    category: "property_manager",
    aliases: ["property management"],
  },
  { label: "HOA board", category: "hoa", aliases: ["hoa"] },
  {
    label: "Mortgage lender",
    category: "mortgage_lender",
    aliases: ["mortgage", "loan officer", "lender"],
  },
  {
    label: "Home inspector",
    category: "home_inspector",
    aliases: ["home inspection", "inspector"],
  },
  {
    label: "General contractor",
    category: "contractor_trade",
    aliases: ["contractor", "builder", "home builder", "remodeler"],
  },
  { label: "Plumber", category: "contractor_trade", aliases: ["plumbing"] },
  {
    label: "Electrician",
    category: "contractor_trade",
    aliases: ["electrical", "electrical contractor"],
  },
  {
    label: "HVAC",
    category: "contractor_trade",
    aliases: ["heating and air", "heating & air", "air conditioning", "ac"],
  },
  { label: "Painter", category: "contractor_trade", aliases: ["painting"] },
  {
    label: "Landscaper",
    category: "contractor_trade",
    aliases: ["landscaping", "lawn care"],
  },
  {
    label: "Tree service",
    category: "contractor_trade",
    aliases: ["tree removal", "arborist"],
  },
  { label: "Gutters", category: "contractor_trade", aliases: ["gutter"] },
  {
    label: "Windows and doors",
    category: "contractor_trade",
    aliases: ["windows", "doors", "windows & doors"],
  },
  { label: "Siding", category: "contractor_trade", aliases: [] },
  { label: "Flooring", category: "contractor_trade", aliases: ["floors"] },
  {
    label: "Concrete",
    category: "contractor_trade",
    aliases: ["concrete contractor"],
  },
  { label: "Fencing", category: "contractor_trade", aliases: ["fence"] },
  { label: "Handyman", category: "contractor_trade", aliases: [] },
  {
    label: "Restoration",
    category: "contractor_trade",
    aliases: ["water damage", "fire damage", "water restoration"],
  },
  { label: "Solar", category: "contractor_trade", aliases: [] },
  {
    label: "Pest control",
    category: "other",
    aliases: ["exterminator", "pest"],
  },
  { label: "Pool and spa", category: "other", aliases: ["pool", "pools"] },
  { label: "Cleaning", category: "other", aliases: ["cleaning service"] },
  { label: "Attorney", category: "other", aliases: ["lawyer", "law firm"] },
  {
    label: "Networking group",
    category: "networking",
    aliases: ["bni", "chamber of commerce", "networking"],
  },
];

const key = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, " ");

const byKey = new Map<string, LineOfWork>();
for (const entry of LINES_OF_WORK) {
  byKey.set(key(entry.label), entry);
  for (const alias of entry.aliases) byKey.set(key(alias), entry);
}

/** The listed line of work this is another way of saying, if any. */
export function findLineOfWork(
  value: string | null | undefined,
): LineOfWork | null {
  if (!value) return null;
  return byKey.get(key(value)) ?? null;
}

/**
 * A line of work ready to store, or nothing.
 *
 * One the list knows comes back as the list writes it. Anything else is kept
 * as typed, tidied, with a capital first letter, because it is a real answer
 * the list did not think of.
 */
export function normalizeLineOfWork(
  value: string | null | undefined,
): string | null {
  if (typeof value !== "string") return null;
  const tidy = value.trim().replace(/\s+/g, " ");
  if (tidy === "") return null;
  const listed = findLineOfWork(tidy);
  if (listed) return listed.label;
  return tidy.charAt(0).toUpperCase() + tidy.slice(1);
}

/**
 * What a card is filed under in the card file.
 *
 * Its own line of work when it has one. A card saved before lines of work
 * existed, or one the reader could not place, falls back to its category's
 * name, so it is still filed somewhere sensible rather than under a blank.
 */
export function partnerLineOfWork(partner: {
  line_of_work?: string | null;
  category?: string | null;
}): string {
  return (
    normalizeLineOfWork(partner.line_of_work) ??
    partnerCategoryLabel(partner.category)
  );
}
